import { useEffect, useRef, RefObject } from 'react';

/**
 * Returns a ref to attach to a numeric input element.
 *
 * A focused number input spins its value on the scroll-wheel. We stop that
 * without the usual side effects: rather than blurring the input or swallowing
 * the wheel event (which would freeze the page), we cancel only the value-spin
 * and forward the scroll to the page. The value stays put, focus is retained,
 * and the page keeps scrolling.
 */
export function useDisableWheel<T extends HTMLElement = HTMLInputElement>(): RefObject<T> {
	const ref = useRef<T | null>(null);

	useEffect(() => {
		const el = ref.current;
		const handleWheel = (e: WheelEvent) => {
			// Only intervene while focused — an unfocused input already scrolls
			// the page and leaves its value untouched.
			if (document.activeElement !== el) {
				return;
			}
			e.preventDefault();
			window.scrollBy({ top: e.deltaY });
		};

		el?.addEventListener('wheel', handleWheel, { passive: false });

		return () => {
			el?.removeEventListener('wheel', handleWheel);
		};
	}, []);

	return ref;
}
